// ============================================================
// THE BODY'S AURAS (plan body-map T2; the bench's trait effects, ruled
// 2026-10-09). The looks that hang round a body's nature, each its own
// channel and colour so they stack:
//
//   Regeneration (X)  a ring of green-gold light climbs it, feet to head,
//                     re-drawing the lines it crosses; faster, brighter and
//                     with more sparks as X grows; a faint copy lingers from 5
//   Toxic (X)         a wave of poison through it from the heart with every
//                     beat, quicker and further with X; its natural weapons'
//                     veins pulse with it (and a hit bursts through it, its
//                     blood drains green: ./TraitLayers)
//   Fear (X)          outlines of itself growing outward, fainter each - subtle
//                     even at 4 (user)
//   Warp Conduit      copies of it pour out of its chest and grow past it
//   From Beyond       a window to the void: the figure pitch black, a sky fixed
//                     in space seen through it
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Box } from './frame';
import { hash01 } from './hash';
import { frac, loop } from './tracks';

/** A duration to the hundredth of a second (no float noise in the clock's tracks). */
const secs = (n: number) => Number(n.toFixed(2));

/** A spark's orbit round the ring (its own centre at 0): 25 points on the ellipse, as looping tracks. */
function orbit(rx: number, ry: number, dur: number, offset: number) {
  const at = Array.from({ length: 25 }, (_, i) => Math.PI + (i / 24) * Math.PI * 2);
  return {
    cx: loop(
      'cx',
      at.map((a) => Math.round(rx * Math.cos(a))),
      dur,
      { offset },
    ),
    cy: loop(
      'cy',
      at.map((a) => Math.round(ry * Math.sin(a))),
      dur,
      { offset },
    ),
  };
}

/** Regeneration (X) round a figure with these bounds: the ring, its climb, its sparks and the motes up the body. */
export function regenLook(x: number, [x0, y0, x1, y1]: Box) {
  const [w, h] = [x1 - x0, y1 - y0];
  const rx = w * 0.62;
  const ry = rx * 0.2;
  const spark = secs(3 - 0.15 * x);
  const mote = secs(4 - 0.2 * x);
  return {
    cx: (x0 + x1) / 2,
    rx,
    ry,
    /** It climbs from below the feet to above the head, and again. */
    from: y1 + ry,
    to: y0 - ry,
    dur: Number((5.5 - 0.35 * x).toFixed(2)),
    opacity: Number((0.55 + 0.045 * x).toFixed(3)),
    ring: 10 + x,
    lineWidth: 30 + 4 * x,
    lingers: x >= 5,
    sparks: Array.from({ length: 2 + Math.ceil(x / 2) }, (_, k) => ({
      r: 9 + x * 0.6,
      ...orbit(rx, ry, spark, k * 0.53),
    })),
    motes: Array.from({ length: x }, (_, k) => ({
      cx: x0 + w * (0.25 + 0.5 * frac(k * 0.618)),
      cy: loop('cy', [y1, y0 + h * 0.2], mote, { offset: k * 0.7 }),
      opacity: loop('opacity', [0, 0.9, 0], mote, { offset: k * 0.7 }),
    })),
  };
}

/** Where the regeneration ring is at `t`: its height. */
export const regenAt = (look: ReturnType<typeof regenLook>, t: number) =>
  look.from + (look.to - look.from) * frac(t / look.dur);

/** Toxic (X): the beat - its resting glow round the heart and the wave out with each beat; its weapons' veins. */
export function toxicLook(x: number) {
  const beat = secs(1.5 - 0.15 * x);
  return {
    rest: 160 + 50 * x,
    wave: { r: loop('r', [120, 900 + 350 * x], beat), opacity: loop('opacity', [1, 0], beat) },
    /** The fists' veins: the poison creeps up from the hands and ebbs (a band's two edges), pulsing. */
    veins: {
      dark: loop('offset', [0.62, 0.18, 0.62], 3.2, { mode: 'ease' }),
      light: loop('offset', [0.74, 0.32, 0.74], 3.2, { mode: 'ease' }),
      pulse: loop('opacity', [0.65, 1, 0.65], 1.6),
    },
  };
}

/** How many outlines Fear (X) stands in: one, and one more a step. */
export const fearRims = (x: number) => 1 + x;

/** How much each outline grows on its way to the next. */
const FEAR_STEP = 1.1;

/**
 * Fear (X) at `t`: its outlines' growth and each one's opacity. Each loop grows them one step outward while every
 * outline fades to the next one's opacity, so the loop's end is the next loop's start - it never jumps.
 */
export function fearAt(x: number, [x0, y0, x1, y1]: Box, t: number) {
  const p = frac(t / (9 - x));
  const op = (d: number) => Math.max(0, (0.1 + 0.03 * x) * 0.55 ** d);
  return {
    centre: [(x0 + x1) / 2, (y0 + y1) / 2] as const,
    step: FEAR_STEP,
    scale: 1 + (FEAR_STEP - 1) * p,
    rims: Array.from({ length: fearRims(x) }, (_, k) => op(k) + (op(k + 1) - op(k)) * p),
  };
}

/** Warp Conduit at `t`: four copies a second apart, each growing from a speck out of its chest, flaring and fading. */
export function conduitAt(t: number): { scale: number; opacity: number }[] {
  return [0, 1, 2, 3].map((k) => {
    const p = frac((t + k) / 4);
    const opacity = p < 1 / 3 ? 1.65 * p : p < 2 / 3 ? 0.55 - 0.6 * (p - 1 / 3) : 0.35 - 1.05 * (p - 2 / 3);
    return { scale: 0.06 + 1.34 * p, opacity: Math.max(0, opacity) };
  });
}

/** One point of light in the void: where, how big, which of its colours, and whether it glows. */
interface VoidStar {
  x: number;
  y: number;
  r: number;
  tint: number;
  big?: boolean;
}

function skyOf([x0, y0, x1, y1]: Box) {
  const [w, h] = [x1 - x0, y1 - y0];
  const X = (f: number) => x0 + w * f;
  const Y = (f: number) => y0 + h * f;
  return {
    nebulae: (
      [
        [0.25, 0.6, 0.3, 0.1, 0.32],
        [0.72, 0.28, 0.26, 0.08, 0.26],
        [0.6, 0.82, 0.2, 0.06, 0.18],
      ] as const
    ).map(([fx, fy, rx, ry, opacity], tint) => ({ cx: X(fx), cy: Y(fy), rx: w * rx, ry: h * ry, opacity, tint })),
    galaxy: { x: X(0.58), y: Y(0.36), stars: galaxyArms() },
    cluster: Array.from({ length: 40 }, (_, k) => ({
      x: X(0.3) + (hash01(k, 2) - 0.5) * 160 * hash01(k, 9),
      y: Y(0.7) + (hash01(k, 4) - 0.5) * 160 * hash01(k, 9),
      r: 2 + 3 * hash01(k, 5),
      tint: 1,
    })),
    stars: fieldOfStars(X, Y),
  };
}

const skies = new WeakMap<Box, ReturnType<typeof skyOf>>();

/**
 * What shows through the window to the void, laid out round the figure's box: three nebulae, a tilted two-armed
 * galaxy, a star cluster, and a field of stars of many sizes and four colours. Fixed - nothing in it moves.
 */
export function voidSky(box: Box): ReturnType<typeof skyOf> {
  // Hundreds of seeded stars: worked out once per box (an art's frame keeps one box), not on every render.
  if (!skies.has(box)) skies.set(box, skyOf(box));
  return skies.get(box)!;
}

/** The field of stars round the box: many small, a few big ones that glow, in four colours. */
function fieldOfStars(X: (f: number) => number, Y: (f: number) => number): VoidStar[] {
  return Array.from({ length: 340 }, (_, k) => {
    const big = hash01(k, 11) > 0.94;
    return {
      x: X(-0.1 + 1.2 * hash01(k, 1)),
      y: Y(-0.05 + 1.1 * hash01(k, 3)),
      r: big ? 9 + 5 * hash01(k, 6) : 2 + 4 * hash01(k, 6) ** 2,
      tint: Math.floor(hash01(k, 13) * 4),
      big,
    };
  });
}

/** The galaxy's two arms round its core, in its own frame (tilted and squashed where it is drawn). */
function galaxyArms(): (VoidStar & { opacity: number })[] {
  return [0, Math.PI].flatMap((arm) =>
    Array.from({ length: 70 }, (_, k) => {
      const a = arm + k * 0.09;
      const rr = 30 * Math.exp(0.034 * k * 2.2);
      return {
        x: rr * Math.cos(a) + (hash01(k, arm) - 0.5) * 14,
        y: rr * Math.sin(a) + (hash01(k, arm + 3) - 0.5) * 14,
        r: 3 + 4 * hash01(k, arm + 7),
        tint: k % 9 ? 1 : 3,
        opacity: Number((1 - k / 80).toFixed(2)),
      };
    }),
  );
}
